/**
 * 「日本語から翻訳」ボタン (Issue #492 / SBI-11)。
 *
 * 多言語入力欄 (`LocalizedTextFields`) の上部に置き、日本語の原文を Amazon Translate
 * で各言語へ翻訳して **下書きとして** 入力欄へ反映する。
 *
 * ## ⚠️ 自動保存はしない (誤案内防止)
 *
 * 本ボタンが行うのは「入力欄の値を書き換える」ところまで。機械翻訳の訳文を
 * そのまま放送すると誤案内 (乗客の乗り間違い等) に直結するため、
 *
 *   1. サーバ (`POST /translate`) は翻訳結果を一切保存しない
 *   2. 本コンポーネントも保存 API を呼ばない (`onChange` でフォーム状態を更新するだけ)
 *   3. 実際の保存はモーダルの「保存」ボタン (人手修正後の明示操作) で行う
 *
 * という 3 段構えで担保する。ユーザーにもこの前提が伝わるようヒント文を併記する。
 *
 * ## 既存入力の上書きについて
 *
 * すでに訳文が入っている言語欄も上書きできる (「翻訳し直す」需要が主だから)。
 * ただし人手修正を失う操作なので、**既存の訳文がある場合は確認を挟む**。
 * `window.confirm` ではなくインラインの確認 UI にして、モーダル内での操作が
 * 中断されないようにする。
 */
import { useState } from 'react';

import { useTranslate } from '../../../contexts/TranslateContext';
import type { LocalizedText } from '../../../utils/localizedSsml';
import { repairSsmlWrapper } from '../../../utils/localizedSsml';
import { describeApiErrorMessage } from '../../../utils/apiErrorMessage';

/**
 * `failed[].reason` を運用者向けの日本語メッセージへ変換する。
 *
 * 未知の理由でも「何が起きたか」が伝わるよう、生の例外名を併記してフォールバックする
 * (Amazon Translate の例外は将来増え得るため、網羅を前提にしない)。
 */
export function describeTranslateFailure(reason: string): string {
  if (reason === 'UnsupportedLanguage') {
    // 別言語で代替すると誤案内になるため、意図的に翻訳していないことを明示する。
    return '自動翻訳に対応していない言語です（手入力してください）';
  }
  if (reason === 'ThrottlingException') {
    return '翻訳の実行回数制限に達しました（少し待って再実行してください）';
  }
  if (reason === 'UnsupportedLanguagePairException') {
    return '日本語からこの言語への翻訳に対応していません（手入力してください）';
  }
  if (reason === 'TextSizeLimitExceededException') {
    return '原文が長すぎます（短く分割してください）';
  }
  return `翻訳に失敗しました（${reason}）`;
}

export function TranslateFromJapaneseButton({
  sourceText,
  targetLanguages,
  value,
  onChange,
  testId,
}: {
  /** 翻訳元となる日本語の原文 (空 / 空白のみならボタンは無効) */
  sourceText: string;
  /**
   * 翻訳先の language_key 配列 (日本語を除いた、画面に表示されている言語欄)。
   * 空配列ならボタンは表示しない (翻訳する先が無い)。
   */
  targetLanguages: readonly string[];
  /** 現在の各言語の入力値 (既存訳文の有無判定と、マージ後の値の組み立てに使う) */
  value: LocalizedText;
  /** 翻訳結果を反映する (**保存はしない**。フォーム状態の更新のみ) */
  onChange: (next: LocalizedText) => void;
  /** `data-testid` の接尾辞。入力欄ごとに一意にする */
  testId: string;
}): React.JSX.Element | null {
  const { translate } = useTranslate();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [translatedCount, setTranslatedCount] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);

  // 翻訳機能が無効 (Provider 未設置 / ハンドラ未提供) / 翻訳先が無い場合は何も出さない。
  if (!translate || targetLanguages.length === 0) return null;

  const trimmedSource = sourceText.trim();
  const hasSource = trimmedSource !== '';
  // 既存の訳文があるかどうか (上書き確認を出すかの判定)。
  const hasExisting = targetLanguages.some((language) => (value[language] ?? '').trim() !== '');

  const run = async (): Promise<void> => {
    setConfirming(false);
    setRunning(true);
    setError(null);
    setWarnings([]);
    setTranslatedCount(null);
    try {
      const result = await translate({ sourceText: trimmedSource, targetLanguages });
      /*
       * Issue #555 (不具合 C): 訳文の `</speak>` の外へ句読点が出ていたら
       * **下書きとして反映する時点で** 内側へ戻す。
       *
       * サーバ側 (`/translate`) が SSML タグを保護するようになったため通常は
       * 既に整形式だが、二重の防衛線を張る:
       *   - 壊れた下書きをそのまま見せると、運用者が気付かず保存 → 400 で
       *     弾かれる、という遠回りになる
       *   - 旧バージョンのサーバ (デプロイ前) が返した訳文でも画面側で直る
       *
       * プレーンテキスト (`<speak>` を含まない文面) は `repairSsmlWrapper` が
       * そのまま返すため、任意テキストの通常テキスト欄には影響しない。
       */
      const entries = Object.entries(result.translations).map(
        ([language, text]) => [language, repairSsmlWrapper(text)] as const,
      );
      if (entries.length > 0) {
        // ⚠️ ここで行うのは **フォーム状態の更新だけ**。保存 API は呼ばない。
        // 既存値を保持したままマージするので、翻訳できなかった言語の手入力は消えない。
        onChange({ ...value, ...Object.fromEntries(entries) });
      }
      setTranslatedCount(entries.length);
      setWarnings(
        result.failed.map(
          (failure) => `${failure.language}: ${describeTranslateFailure(failure.reason)}`,
        ),
      );
    } catch (failure) {
      setError(
        describeApiErrorMessage(failure, '翻訳に失敗しました。時間をおいて再実行してください。'),
      );
    } finally {
      setRunning(false);
    }
  };

  const handleClick = (): void => {
    // 既存の訳文 (人手修正を含み得る) を上書きする前に確認する。
    if (hasExisting && !confirming) {
      setConfirming(true);
      return;
    }
    void run();
  };

  return (
    <div className="translate-from-japanese" style={{ margin: '4px 0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid={`translate-from-japanese-${testId}`}
          disabled={running || !hasSource}
          title={
            hasSource
              ? '日本語の文面をもとに各言語欄へ翻訳を下書きします（保存はされません）'
              : '日本語の文面を入力すると翻訳できます'
          }
          onClick={handleClick}
        >
          {running ? '翻訳中...' : '日本語から翻訳'}
        </button>
        {hasSource ? (
          <span className="hint" data-testid={`translate-draft-hint-${testId}`}>
            翻訳結果は下書きです。内容を確認・修正してから保存してください（自動保存されません）
          </span>
        ) : (
          <span className="hint" data-testid={`translate-disabled-hint-${testId}`}>
            日本語の文面を入力すると翻訳できます
          </span>
        )}
      </div>

      {confirming ? (
        <div
          className="hint"
          role="alert"
          data-testid={`translate-overwrite-confirm-${testId}`}
          style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span>入力済みの翻訳を上書きします。よろしいですか？</span>
          <button
            type="button"
            data-testid={`translate-overwrite-ok-${testId}`}
            onClick={() => void run()}
          >
            上書きして翻訳
          </button>
          <button
            type="button"
            data-testid={`translate-overwrite-cancel-${testId}`}
            onClick={() => setConfirming(false)}
          >
            キャンセル
          </button>
        </div>
      ) : null}

      {translatedCount !== null ? (
        <div className="hint" data-testid={`translate-result-${testId}`}>
          {translatedCount > 0
            ? `${translatedCount} 言語を下書きしました（保存はまだされていません）`
            : '翻訳できた言語がありませんでした'}
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="error-message" role="alert" data-testid={`translate-warnings-${testId}`}>
          {warnings.map((warning) => (
            <div key={warning}>{warning}</div>
          ))}
        </div>
      ) : null}

      {error ? (
        <div className="error-message" role="alert" data-testid={`translate-error-${testId}`}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
